import * as React from 'react'
import { lazy } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import {
  RequestDetailIndexDocument,
  TrainingClassDetailIndexPageDocument,
  TrainingDetailPageDocument,
  TrainingSessionInstructorFragment,
  TrainingSurveysDocument,
  TrainingTemplateDocument,
  TrainingsTabsDocument,
} from 'generated/resources/graphql'

import { checkFeatures } from 'modules/core/hooks/useFeatures'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import {
  NavigationRoutesFn,
  buildModuleRoute,
  buildNavigation,
} from 'modules/core/lib/react-router'

import EditTemplateQuestions from 'modules/trainings/pages/Trainings/SurveyTemplates/TrainingTemplateDetails/Edit/EditTemplateQuestions'

import { buildTrainingFormEditHeaderProps } from './ResourceHeaders/trainingFormEditHeader'
import { buildOverviewFormHeaderProps } from './ResourceHeaders/trainingFormOverviewHeader'
import { buildOverviewFormTemplateHeaderProps } from './ResourceHeaders/trainingFormTemplateOverviewHeader'
import { contentRoutes } from './content'

const UniversalImporterTrainingsPage = lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/UniversalImporterTrainingsPage')
)

const UniversalImporterTrainingCoursesPage = lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/UniversalImporterTrainingCoursesPage')
)

const RequestHeader = lazy(() => import('modules/trainings/components/Requests/RequestHeader'))
const PublishedQuestions = lazy(
  () => import('modules/trainings/components/Revamp/Form/Questions/Published')
)

const ClassDetailHeader = lazy(
  () => import('modules/trainings/components/Revamp/TrainingsDetail/Classes/ClassDetailHeader')
)
const TrainingDetailHeader = lazy(
  () => import('modules/trainings/components/Revamp/TrainingsDetail/TrainingDetailHeader')
)

type Navigation = Parameters<NavigationRoutesFn>[0]
type Routes = ReturnType<NavigationRoutesFn>

const RootPage = React.lazy(() => import('modules/trainings/pages/RootPage'))
export const trainingsRoutes = buildNavigation((navigation) => {
  return [
    buildModuleRoute({
      path: navigation.trainings.path,
      element: <RootPage />,
      handle: {
        module: 'new-trainings',
        moduleName: () => i18n.t('sidebar.trainings'),
        meta({ watchQuery, navigate, location }) {
          return watchQuery({
            query: TrainingsTabsDocument,
          }).map(({ data }) => {
            if (location.pathname === navigation.trainings.fullPath()) {
              if (data?.trainings._policies.trainingsAccessManagementSpace.can) {
                navigate(navigation.trainings.list.fullPath())
              } else {
                navigate(navigation.myTrainings.revamp.overview.fullPath())
              }
            }
            return {
              title: i18n.t('sidebar.trainings'),
              settings: navigation.settings.trainings.fullPath(),
              subNavigation: [
                {
                  title: i18n.t('trainings.trainings_revamp.sidebar_title'),
                  href: navigation.trainings.list.fullPath(),
                  id: 'trainings',
                  isVisible: data?.trainings._policies.trainingsAccessManagementSpace.can,
                },
                {
                  title: i18n.t('trainings.requests_title'),
                  href: navigation.trainings.requests.list.fullPath(),
                  id: 'requests',
                  isVisible: data?.trainings._policies.trainingsAccessManagementSpace.can,
                },
                {
                  title: i18n.t('trainings.insights_title'),
                  href: navigation.trainings.insights.fullPath(),
                  id: 'insights',
                  isVisible:
                    data?.trainings._policies.trainingsAccessManagementSpace.can &&
                    data?.trainings._policies.insightRead.can,
                },
              ],
            }
          })
        },
      },
      children: [...list(navigation), ...insights(navigation), ...requests(navigation)],
    }),
  ]
})

const Categories = React.lazy(() => import('modules/trainings/pages/Trainings/Categories'))
const DeleteCategory = React.lazy(() => import('modules/trainings/pages/Trainings/DeleteCategory'))
const NewCategory = React.lazy(() => import('modules/trainings/pages/Trainings/NewCategory'))
const Forms = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Forms')
)
const TrainingFormSettings = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/EditMode')
)
const FormWizard = React.lazy(() => import('modules/trainings/pages/Trainings/Revamp/FormWizard'))
const CreateSurveyOptionModal = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/CreateSurveyOptionModal')
)
const SelectTemplateFormModal = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/SelectTemplateFormModal')
)
const SurveyTemplates = React.lazy(
  () => import('modules/trainings/pages/Trainings/SurveyTemplates')
)
const TrainingTemplateQuestionsTab = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/SurveyTemplates/TrainingTemplateDetails/TemplateQuestions')
)
const TrainingTemplateDetails = React.lazy(
  () => import('modules/trainings/pages/Trainings/SurveyTemplates/TrainingTemplateDetails')
)
const List = React.lazy(() => import('modules/trainings/pages/Trainings/List'))
const TrainingDetailIndexRevamp = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail')
)
const TrainingFormDetail = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail')
)
const TrainingFormAnswersTab = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/Answers')
)
const TrainingFormEditQuestions = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/EditQuestions')
)

const TrainingFormDetailIndex = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/TrainingFormDetail/TrainingFormDetailIndex')
)

const ClassesTab = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Tab')
)

const ClassDetailsParticipantsTab = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/ParticipantsTab')
)
const TrainingClassMaterials = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/Materials')
)

const ClassDetailsDocumentsTab = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/DocumentsTab')
)

const ClassDocumentsNewFileModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/DocumentsTab/ClassDocumentsNewFileModal')
)

const ClassDetailsCostsTab = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/CostsTab')
)

const TrainingClassMaterialsFileViewer = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/Materials/TrainingClassMaterialsFileViewer')
)

const ClassDocumentsFileViewer = React.lazy(
  () =>
    import('../pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/DocumentsTab/ClassDocumentsFileViewer')
)

const Overview = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Overview')
)
const SidePanelCertificates = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/EmployeeClassesSidepanel/CertificatesViewer')
)
const TrainingMaterialsFileViewer = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Materials/TrainingMaterialsFileViewer')
)
const TrainingMaterialsNewFileModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Materials/TrainingMaterialsNewFileModal')
)
const TrainingMaterialsNewLinkModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Materials/TrainingMaterialsNewLinkModal')
)
const StartTraining = React.lazy(
  () => import('modules/trainings/pages/Trainings/TrainingDetail/StartTraining')
)
const StopTraining = React.lazy(
  () => import('modules/trainings/pages/Trainings/TrainingDetail/StopTraining')
)
const NewClassWizard = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/ClassWizard')
)
const EditTraining = React.lazy(() => import('modules/trainings/pages/Trainings/EditTraining'))
const EditTrainingIndex = React.lazy(
  () => import('modules/trainings/pages/Trainings/EditTraining/EditTrainingIndex')
)
const ExportTrainings = React.lazy(
  () => import('modules/trainings/pages/Trainings/ExportTrainings')
)
const DeleteClass = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/DeleteClass')
)

const EditClassModal = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/EditClassModal')
)

const DuplicateClass = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/DuplicateClass')
)

const NewSessionModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab/NewSessionModal')
)
const TrainingDetailRevamp = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail')
)
const Materials = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Materials')
)
const Documents = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Documents')
)
const DocumentNewFileModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Documents/NewDocumentModal')
)
const DocumentFileViewer = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Documents/DocumentsViewer')
)
const ClassesIndex = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes')
)
const ClassDetail = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail')
)
const SessionsTab = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab')
)

const AddParticipantsToClass = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/ParticipantsTab/AddParticipantsToClassModal')
)

const EditSessionSidepanel = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/SessionsTab/SessionsDatatable/EditSessionSidePanel')
)
const DeleteEvent = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/EventSidepanel/DeleteEvent')
)

const Participants = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants')
)
const AddParticipants = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/AddParticipants')
)
const AssignParticipant = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/AssignParticipant')
)
const AssignParticipants = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/AssignParticipants')
)
const UpdateCompletionDate = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/UpdateCompletionDate')
)
const Certificates = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/EmployeeClassesSidepanel/CertificatesViewer')
)
const DeleteParticipant = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/DeleteParticipant')
)
const DeleteParticipants = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/DeleteParticipants')
)
const EmployeesSidepanel = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/EmployeeClassesSidepanel')
)
const EmployeeCertificateUpload = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Participants/EmployeeClassesSidepanel/UploadCertificateModal')
)

const TrainingWizard = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingWizard')
)

const DeleteTraining = React.lazy(() => import('modules/trainings/pages/Trainings/DeleteTraining'))
const DuplicateTraining = React.lazy(
  () => import('modules/trainings/pages/Trainings/DuplicateTraining')
)
const BulkUpdateHideCatalog = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/List/BulkUpdateHideCatalog')
)
const BulkUpdateDisplayCatalog = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/List/BulkUpdateDisplayCatalog')
)

const TrainingDetailFundaeTab = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Fundae')
)
const EventSidepanelNavigation = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Tab/EventSidepanelNavigation')
)

const AttendanceSidepanel = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Tab/EventSidepanelNavigation/AttendanceSidepanel')
)
const MatchDataModal = React.lazy(
  () => import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/MatchDataModal')
)

const TrainingClassMaterialsNewFileModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/Materials/TrainingClassMaterialsNewFileModal')
)

const TrainingClassMaterialsNewLinkModal = React.lazy(
  () =>
    import('modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/Classes/Detail/Materials/TrainingClassMaterialsNewLinkModal')
)

function list(navigation: Navigation): Routes {
  return [
    {
      path: navigation.trainings.list.path,
      handle: {
        meta: ({ watchQuery }) => {
          return watchQuery({
            query: TrainingsTabsDocument,
          }).map(({ data }) => {
            return {
              title: i18n.t('trainings.trainings_revamp.trainings_table.reviewed_title'),
              description: i18n.t(
                'trainings.trainings_revamp.trainings_table.reviewed_description'
              ),
              illustration: 'design-studio',
              subNavigation: [
                {
                  id: 'list',
                  title: i18n.t('trainings.trainings_revamp.trainings_table.title'),
                  href: navigation.trainings.list.index.fullPath(),
                  isVisible: data?.trainings._policies.trainingsAccessManagementSpace.can,
                },
                {
                  id: 'categories',
                  title: i18n.t('trainings.tags.title'),
                  href: navigation.trainings.list.categories.fullPath(),
                  isVisible: data?.trainings._policies.trainingsAccessManagementSpace.can,
                },
                {
                  id: 'survey_templates',
                  title: i18n.t('trainings.survey_templates.title'),
                  href: navigation.trainings.list.surveyTemplates.list.fullPath(),
                  isVisible: data?.trainings._policies.trainingsAccessManagementSpace.can,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to={navigation.trainings.list.index.fullPath()} />,
        },
        {
          path: navigation.trainings.list.index.path,
          children: [
            {
              path: navigation.trainings.list.index.bulk_update_catalog_visible.path,
              element: <BulkUpdateDisplayCatalog />,
            },
            {
              path: navigation.trainings.list.index.bulk_update_catalog_remove.path,
              element: <BulkUpdateHideCatalog />,
            },
            {
              path: navigation.trainings.list.index.duplicate.path,
              element: <DuplicateTraining />,
            },
            {
              path: navigation.trainings.list.index.delete.path,
              element: <DeleteTraining />,
            },
            {
              path: navigation.trainings.list.index.export.path,
              element: <ExportTrainings />,
            },
            {
              path: navigation.trainings.list.index.new.path,
              element: <TrainingWizard />,
            },
          ],
          element: <List />,
        },
        {
          path: navigation.trainings.list.categories.path,
          element: <Categories />,
          children: [
            {
              path: navigation.trainings.list.categories.new.path,
              element: <NewCategory />,
            },
            {
              path: navigation.trainings.list.categories.deleteCategory.path,
              element: <DeleteCategory />,
            },
          ],
        },
        {
          path: navigation.trainings.list.import.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterTrainingsPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('trainings.trainings_table.import_courses_and_participants_header'),
              headerComponent: (
                <ResourceHeader
                  title={i18n.t('trainings.trainings_table.import_courses_and_participants_header')}
                />
              ),
            }),
          },
        },
        {
          path: navigation.trainings.list.importCourses.path,
          element: (
            <Routes>
              <Route path="" element={<UniversalImporterTrainingCoursesPage />} />
            </Routes>
          ),
          handle: {
            resource: true,
            meta: () => ({
              title: i18n.t('trainings.trainings_table.import_courses_header'),
              headerComponent: (
                <ResourceHeader title={i18n.t('trainings.trainings_table.import_courses_header')} />
              ),
            }),
          },
        },
        ...surveyTemplates(),
        ...detail(),
      ],
    },
  ]

  function surveyTemplates(): Routes {
    return [
      {
        path: navigation.trainings.list.surveyTemplates.path,
        children: [
          {
            index: true,
            element: <Navigate to={navigation.trainings.list.surveyTemplates.list.fullPath()} />,
          },
          {
            path: navigation.trainings.list.surveyTemplates.list.path,
            element: <SurveyTemplates />,
            children: [
              {
                path: navigation.trainings.list.surveyTemplates.list.newTemplate.path,
                element: <FormWizard />,
              },
            ],
          },
          {
            path: navigation.trainings.list.surveyTemplates.templateDetails.path,
            element: <TrainingTemplateDetails />,
            handle: {
              resource: true,
              meta({ watchQuery, params }) {
                return watchQuery({
                  query: TrainingTemplateDocument,
                  fetchPolicy: 'cache-and-network',
                  variables: {
                    id: String(params.templateId),
                  },
                }).map(({ data }) => {
                  const templateForm = data.forms.form
                  if (!templateForm) {
                    return { title: '' }
                  }

                  return {
                    headerComponentProps: buildOverviewFormTemplateHeaderProps({
                      formTemplate: templateForm,
                    }),
                    title:
                      templateForm?.title ||
                      i18n.t('forms.custom_templates.template_details.fallback_title'),
                    subNavigation: [
                      {
                        title: i18n.t('forms.custom_templates.template_details.questions'),
                        index: true,
                        href: navigation.trainings.list.surveyTemplates.templateDetails.questions.fullPath(
                          { templateId: String(params.templateId) }
                        ),
                      },
                    ],
                  }
                })
              },
            },
            children: [
              {
                index: true,
                element: (
                  <Navigate
                    to={navigation.trainings.list.surveyTemplates.templateDetails.questions.path}
                    replace
                  />
                ),
              },
              {
                path: navigation.trainings.list.surveyTemplates.templateDetails.questions.path,
                element: <TrainingTemplateQuestionsTab />,
              },
              {
                path: navigation.trainings.list.surveyTemplates.templateDetails.edit.path,
                element: <EditTemplateQuestions />,
                handle: {
                  resource: true,
                  meta({ watchQuery, params }) {
                    return watchQuery({
                      query: TrainingTemplateDocument,
                      fetchPolicy: 'cache-and-network',
                      variables: {
                        id: String(params.templateId),
                      },
                    }).map(({ data }) => {
                      const trainingForm = data.forms.form
                      if (!trainingForm) {
                        return { title: '' }
                      }

                      return {
                        title: 'Edit',
                        breadcrumb: 'Edit',
                        headerComponentProps: buildTrainingFormEditHeaderProps({
                          form: trainingForm,
                        }),
                      }
                    })
                  },
                },
              },
            ],
          },
        ],
      },
    ]
  }

  function detail(): Routes {
    const DeleteClassWrapperTab = () => {
      const { id } = useParams()

      return (
        <DeleteClass
          back={navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath({
            id: Number(id),
          })}
        />
      )
    }

    const DeleteClassWrapperDetail = () => {
      const { id, classId } = useParams()
      return (
        <DeleteClass
          back={navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath(
            {
              id: Number(id),
              classId: Number(classId),
            }
          )}
        />
      )
    }

    const AttendanceSidepanelDetails = () => {
      track(trackingEvents.training.trainingSession.viewSession)
      return <AttendanceSidepanel />
    }
    return [
      {
        path: navigation.trainings.list.details_revamp.path,
        handle: {
          resource: true,
          meta({ watchQuery, params, session }) {
            return watchQuery({
              query: TrainingDetailPageDocument,
              fetchPolicy: 'cache-and-network',
              variables: {
                trainingId: Number(params.id),
                companyId: session.company.id,
              },
            }).map(({ data }) => {
              const training = data.trainings.training

              const showFundaeTab =
                training?.fundaeSubsidized &&
                data?.companies.legalEntitiesConnection.nodes.some((le) => le.country === 'es')

              const participants =
                data?.trainings?.trainingMembershipsConnection?.nodes?.filter(
                  (node) => node.trainingId === Number(params.id)
                ) ?? []

              const instructors: TrainingSessionInstructorFragment[] =
                training?.trainingClassesConnection?.nodes.flatMap((trainingClass) =>
                  trainingClass?.sessions?.flatMap((s) => s?.sessionInstructors || [])
                ) ?? []

              if (!training) {
                return { title: '' }
              }
              const trainingClasses = training?.trainingClassesConnection.nodes ?? []
              const { has_dev_trainings_scorm_cloud: hasScormFF } = checkFeatures(
                ['dev_trainings_scorm_cloud'],
                data.features?.featuresConnection.nodes
              )
              return {
                headerComponent: (
                  <TrainingDetailHeader
                    hasContentModules={training.contentModulesConnection.nodes.length > 0}
                    contentEnabled={Boolean(
                      data.companies.moduleAvailabilitiesConnection.nodes.some(
                        (x) => x.id === 'lms' && x.available
                      )
                    )}
                    participants={participants}
                    trainingClasses={trainingClasses}
                    training={training}
                    instructors={instructors}
                    isScormTraining={Boolean(training.scormPackage)}
                    hasScormFF={hasScormFF}
                    companyId={session.company.id}
                  />
                ),
                title: training.name,
                subNavigation: [
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.overview'),
                    href: navigation.trainings.list.details_revamp.view.overview.fullPath({
                      id: training.id,
                    }),
                    index: true,
                  },
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.groups'),
                    href: navigation.trainings.list.details_revamp.view.trainingClasses.tab.fullPath(
                      {
                        id: training.id,
                      }
                    ),
                  },
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.participants'),
                    href: navigation.trainings.list.details_revamp.view.participants.fullPath({
                      id: training.id,
                    }),
                  },
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.attachments'),
                    href: navigation.trainings.list.details_revamp.view.attachments.fullPath({
                      id: training.id,
                    }),
                  },
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.documents'),
                    href: navigation.trainings.list.details_revamp.view.documents.fullPath({
                      id: training.id,
                    }),
                  },
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.surveys'),
                    href: navigation.trainings.list.details_revamp.view.surveys.fullPath({
                      id: training.id,
                    }),
                  },
                  {
                    title: i18n.t('trainings.trainings_revamp.training_detail.tabs.fundae'),
                    href: navigation.trainings.list.details_revamp.view.fundae.fullPath({
                      id: training.id,
                    }),
                    isVisible: showFundaeTab,
                  },
                ],
              }
            })
          },
        },
        children: [
          {
            index: true,
            element: <TrainingDetailIndexRevamp />,
          },
          {
            path: navigation.trainings.list.details_revamp.view.path,
            element: <TrainingDetailRevamp />,
            children: [
              {
                path: navigation.trainings.list.details_revamp.view.overview.path,
                element: <Overview />,
              },
              {
                path: navigation.trainings.list.details_revamp.view.participants.path,
                element: <Participants />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.view.participants
                      .previewCertificate.path,
                    element: <Certificates />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants.employees.path,
                    element: <EmployeesSidepanel />,
                    children: [
                      {
                        path: navigation.trainings.list.details_revamp.view.participants.employees
                          .uploadCertificate.path,
                        element: <EmployeeCertificateUpload />,
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.participants
                          .previewCertificate.path,
                        element: <SidePanelCertificates />,
                      },
                    ],
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants.addParticipants
                      .path,
                    element: <AddParticipants />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants
                      .assignParticipant.path,
                    element: <AssignParticipant />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants
                      .assignParticipants.path,
                    element: <AssignParticipants />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants
                      .updateCompletionDate.path,
                    element: <UpdateCompletionDate />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants
                      .deleteParticipant.path,
                    element: <DeleteParticipant />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.participants
                      .deleteParticipants.path,
                    element: <DeleteParticipants />,
                  },
                ],
              },
              {
                path: navigation.trainings.list.details_revamp.view.trainingClasses.path,
                element: <ClassesIndex />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.view.trainingClasses.detail.path,
                    element: <ClassDetail />,
                    handle: {
                      resource: true,
                      meta({ watchQuery, params, session }) {
                        return watchQuery({
                          query: TrainingClassDetailIndexPageDocument,
                          fetchPolicy: 'cache-and-network',
                          variables: {
                            companyId: session.company.id,
                            trainingClassId: Number(params.classId),
                            withFF: true,
                          },
                        }).map(({ data }) => {
                          const group = data.trainings.trainingClass
                          if (!group) return { title: '' }
                          return {
                            headerComponent: <ClassDetailHeader trainingClassInfo={group} />,
                            title: group.name,
                            subNavigation: [
                              {
                                title: i18n.t(
                                  'trainings.trainings_revamp.classes.header.tabs.sessions'
                                ),
                                index: true,
                                href: navigation.trainings.list.details_revamp.view.trainingClasses.detail.sessions.fullPath(
                                  { id: Number(params.id), classId: Number(params.classId) }
                                ),
                              },
                              {
                                title: i18n.t(
                                  'trainings.trainings_revamp.classes.header.tabs.participants'
                                ),
                                href: navigation.trainings.list.details_revamp.view.trainingClasses.detail.participants.fullPath(
                                  { id: Number(params.id), classId: Number(params.classId) }
                                ),
                              },
                              {
                                title: i18n.t(
                                  'trainings.trainings_revamp.classes.header.tabs.materials'
                                ),
                                href: navigation.trainings.list.details_revamp.view.trainingClasses.detail.materials.fullPath(
                                  { id: Number(params.id), classId: Number(params.classId) }
                                ),
                              },
                              {
                                title: i18n.t(
                                  'trainings.trainings_revamp.classes.header.tabs.documents'
                                ),
                                href: navigation.trainings.list.details_revamp.view.trainingClasses.detail.documents.fullPath(
                                  { id: Number(params.id), classId: Number(params.classId) }
                                ),
                              },
                              {
                                title: i18n.t(
                                  'trainings.trainings_revamp.classes.header.tabs.costs'
                                ),
                                href: navigation.trainings.list.details_revamp.view.trainingClasses.detail.costs.fullPath(
                                  { id: Number(params.id), classId: Number(params.classId) }
                                ),
                              },
                            ],
                          }
                        })
                      },
                    },
                    children: [
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.detail
                          .sessions.path,
                        element: <SessionsTab />,
                        children: [
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.sessions.editClass.path,
                            element: <EditClassModal />,
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.sessions.deleteClass.path,
                            element: <DeleteClassWrapperDetail />,
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.sessions.delete.path,
                            element: <DeleteEvent />,
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.sessions.create.path,
                            element: <NewSessionModal />,
                          },

                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.sessions.attendance.path,
                            element: <EventSidepanelNavigation sidepanel="attendance" />,
                            children: [
                              {
                                path: navigation.trainings.list.details_revamp.view.trainingClasses
                                  .detail.sessions.attendance.event.path,
                                element: <AttendanceSidepanelDetails />,
                              },
                            ],
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.sessions.edit.path,
                            element: <EditSessionSidepanel />,
                          },
                        ],
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.detail
                          .participants.path,
                        element: <ClassDetailsParticipantsTab />,
                        children: [
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.participants.add.path,
                            element: <AddParticipantsToClass />,
                          },
                        ],
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.detail
                          .materials.path,
                        element: <TrainingClassMaterials />,
                        children: [
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.materials.newFile.path,
                            element: <TrainingClassMaterialsNewFileModal />,
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.materials.newLink.path,
                            element: <TrainingClassMaterialsNewLinkModal />,
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.materials.previewFile.path,
                            element: <TrainingClassMaterialsFileViewer />,
                          },
                        ],
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.detail
                          .documents.path,
                        element: <ClassDetailsDocumentsTab />,
                        children: [
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.documents.newFile.path,
                            element: <ClassDocumentsNewFileModal />,
                          },
                          {
                            path: navigation.trainings.list.details_revamp.view.trainingClasses
                              .detail.documents.previewFile.path,
                            element: <ClassDocumentsFileViewer />,
                          },
                        ],
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.detail
                          .costs.path,
                        element: <ClassDetailsCostsTab />,
                      },
                    ],
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.trainingClasses.tab.path,
                    element: <ClassesTab />,
                    children: [
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.tab
                          .editClassModal.path,
                        element: <EditClassModal />,
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.tab
                          .deleteClass.path,
                        element: <DeleteClassWrapperTab />,
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.tab
                          .newClass.path,
                        element: <NewClassWizard />,
                      },
                      {
                        path: navigation.trainings.list.details_revamp.view.trainingClasses.tab
                          .duplicateClass.path,
                        element: <DuplicateClass />,
                      },
                    ],
                  },
                ],
              },
              {
                path: navigation.trainings.list.details_revamp.view.attachments.path,
                element: <Materials />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.view.attachments.newFile.path,
                    element: <TrainingMaterialsNewFileModal />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.attachments.previewFile
                      .path,
                    element: <TrainingMaterialsFileViewer />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.attachments.newLink.path,
                    element: <TrainingMaterialsNewLinkModal />,
                  },
                ],
              },
              {
                path: navigation.trainings.list.details_revamp.view.documents.path,
                element: <Documents />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.view.documents.newFile.path,
                    element: <DocumentNewFileModal />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.documents.previewFile.path,
                    element: <DocumentFileViewer />,
                  },
                ],
              },
              {
                path: navigation.trainings.list.details_revamp.view.startTraining.path,
                element: <StartTraining />,
              },
              {
                path: navigation.trainings.list.details_revamp.view.stopTraining.path,
                element: <StopTraining />,
              },
              {
                path: navigation.trainings.list.details_revamp.view.surveys.path,
                element: <Forms />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.view.surveys.select.path,
                    element: <CreateSurveyOptionModal />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.surveys.newSurvey.path,
                    element: <FormWizard />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.surveys.selectCustomTemplate
                      .path,
                    element: <SelectTemplateFormModal />,
                  },
                ],
              },
              // This is for specific usage of Spanish customers
              {
                path: navigation.trainings.list.details_revamp.view.fundae.path,
                element: <TrainingDetailFundaeTab />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.view.fundae.educationLevel.path,
                    element: <MatchDataModal category="educationlevel" />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.view.fundae.professionalCategory
                      .path,
                    element: <MatchDataModal category="professionalcategory" />,
                  },
                ],
              },
            ],
          },
          {
            path: navigation.trainings.list.details_revamp.editTraining.path,
            element: <EditTrainingIndex />,
            handle: {
              resource: true,
              meta: () => ({
                title: i18n.t('trainings.edit_mode.title'),
              }),
            },
            children: [
              {
                path: navigation.trainings.list.details_revamp.editTraining.view.path,
                element: <EditTraining />,
              },
            ],
          },
          {
            path: navigation.trainings.list.details_revamp.trainingSurveyDetail.path,
            handle: {
              resource: true,
              meta({ watchQuery, params }) {
                return watchQuery({
                  query: TrainingSurveysDocument,
                  fetchPolicy: 'cache-and-network',
                  variables: {
                    id: String(params.formId),
                    trainingId: Number(params.id),
                  },
                }).map(({ data }) => {
                  const trainingForm = data.forms.form
                  if (!trainingForm) {
                    return { title: '' }
                  }
                  const groups = data.trainings.trainingClassesConnection.nodes.filter((x) =>
                    x.materializedFormsConnection.nodes.some(
                      (y) => String(y.form?.id) === String(trainingForm.id)
                    )
                  )

                  const respondents = Array.from(
                    new Map(
                      data?.forms?.membershipsConnection?.edges
                        ?.map((edge) => edge.node)
                        .filter(Boolean)
                        .map((respondent) => [respondent.id, respondent])
                    ).values()
                  )
                  return {
                    headerComponentProps: buildOverviewFormHeaderProps({
                      form: trainingForm,
                      groups: groups,
                      respondents: respondents,
                      trainingStatus: data?.trainings.training?.status ?? '',
                    }),
                    title: data?.forms.form?.title || i18n.t('trainings.surveys.fallback_title'),
                    subNavigation: [
                      {
                        title: i18n.t('trainings.surveys.create.overview'),
                        index: true,
                        href: navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.fullPath(
                          {
                            id: String(trainingForm?.resourceId),
                            formId: String(params.formId),
                          }
                        ),
                      },
                      {
                        title: i18n.t('trainings.surveys.tabs.responses'),
                        href: navigation.trainings.list.details_revamp.trainingSurveyDetail.responses.fullPath(
                          { id: String(trainingForm?.resourceId), formId: String(params.formId) }
                        ),
                      },
                    ],
                  }
                })
              },
            },
            children: [
              { index: true, element: <TrainingFormDetailIndex /> },
              {
                path: navigation.trainings.list.details_revamp.trainingSurveyDetail.settings.path,
                element: <TrainingFormSettings />,
                handle: {
                  meta: () => ({
                    title: 'Settings',
                  }),
                },
              },
              {
                path: '',
                element: <TrainingFormDetail />,
                children: [
                  {
                    path: navigation.trainings.list.details_revamp.trainingSurveyDetail.questions
                      .path,
                    element: <PublishedQuestions />,
                  },

                  {
                    path: navigation.trainings.list.details_revamp.trainingSurveyDetail.responses
                      .path,
                    element: <TrainingFormAnswersTab />,
                  },
                  {
                    path: navigation.trainings.list.details_revamp.trainingSurveyDetail.edit.path,
                    element: (() => {
                      return <TrainingFormEditQuestions />
                    })(),
                    handle: {
                      resource: true,
                      meta({ watchQuery, params }) {
                        return watchQuery({
                          query: TrainingSurveysDocument,
                          fetchPolicy: 'cache-and-network',
                          variables: {
                            id: String(params.formId),
                            trainingId: Number(params.id),
                          },
                        }).map(({ data }) => {
                          const trainingForm = data.forms.form
                          if (!trainingForm) {
                            return { title: '' }
                          }

                          return {
                            title: 'Edit',
                            breadcrumb: 'Edit',
                            headerComponentProps: buildTrainingFormEditHeaderProps({
                              form: trainingForm,
                            }),
                          }
                        })
                      },
                    },
                  },
                ],
              },
              {
                path: navigation.trainings.list.details_revamp.trainingSurveyDetail.settings.path,
                element: <TrainingFormSettings />,
                handle: {
                  meta: () => ({
                    title: 'Settings',
                  }),
                },
              },
            ],
          },
          ...contentRoutes(navigation),
        ],
      },
    ]
  }
}

const Insights = React.lazy(() => import('modules/trainings/pages/Trainings/Insights'))
function insights(navigation: Navigation): Routes {
  return [
    {
      path: navigation.trainings.insights.path,
      element: <Insights />,
      handle: {
        meta: () => {
          return {
            title: i18n.t('trainings.insights.title'),
            description: i18n.t('trainings.insights.description'),
            illustration: 'user-interaction-review_chart',
          }
        },
      },
    },
  ]
}

const Requests = React.lazy(() => import('modules/trainings/pages/Trainings/Requests'))
const RequestDetail = React.lazy(() => import('modules/trainings/pages/Trainings/RequestDetail'))

const RequestDetailIndex = React.lazy(
  () => import('modules/trainings/pages/Trainings/RequestDetailIndex')
)

function requests(navigation: Navigation): Routes {
  return [
    {
      path: navigation.trainings.requests.path,
      handle: {
        meta: () => {
          return {
            illustration: 'document-folder-board-edit',
            title: i18n.t('trainings.trainings_requests_title'),
            description: i18n.t('trainings.training_requests.requests.description'),
          }
        },
      },
      children: [
        {
          element: <Navigate to={navigation.trainings.requests.list.fullPath()} />,
          index: true,
        },
        {
          path: navigation.trainings.requests.list.path,
          element: <Requests />,
        },
        {
          path: navigation.trainings.requests.detail.path,
          element: <RequestDetailIndex />,
          handle: {
            resource: true,
            meta({ watchQuery, params }) {
              return watchQuery({
                query: RequestDetailIndexDocument,
                fetchPolicy: 'network-only',
                variables: {
                  ids: [Number(params.id)],
                },
              }).map(({ data }) => {
                const request = data.trainings.trainingRequestsConnection.nodes.find(
                  (node) => String(node.id) === params.id
                )
                if (!request) {
                  return { title: '' }
                }
                return {
                  headerComponent: <RequestHeader trainingRequest={request} />,
                  title: request?.trainingName,
                }
              })
            },
          },
          children: [
            {
              path: navigation.trainings.requests.detail.view.path,
              element: <RequestDetail back={navigation.trainings.requests.list.fullPath()} />,
            },
          ],
        },
      ],
    },
  ]
}
